import { Alert, Button, Modal, Select, Space, Typography } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router-dom";
import { CustomTextField } from "~/components";
import type { TenantDetailsGenerateShadowInviteResult } from "~/types/tenantDetailsAction";
import * as APITypes from "~/API";
import { INTENTS, buildTenantShadowInviteUrl } from "~/utils";
import { ENVS } from "~/utils/constants";

type LeaseOption = { leaseId: string; label: string };
type StayOption = { stayId: string; label: string };

type ContextValue =
  | { kind: "lease"; id: string }
  | { kind: "stay"; id: string };

function parseContextValue(value: string | undefined): ContextValue | null {
  if (!value) return null;
  if (value.startsWith("lease:")) {
    return { kind: "lease", id: value.slice("lease:".length) };
  }
  if (value.startsWith("stay:")) {
    return { kind: "stay", id: value.slice("stay:".length) };
  }
  return null;
}

export const InviteShadowTenantButton = ({
  tenantId,
  tenantType,
  leaseOptions,
  stayOptions = [],
  formAction,
}: {
  tenantId: string;
  tenantType?: APITypes.TenantType;
  leaseOptions: LeaseOption[];
  stayOptions?: StayOption[];
  /** Parent tenant-details route (e.g. `/tenant/:id`) so the action runs on `TenantDetailsPageAction` when nested under `/lease`. */
  formAction: string;
}) => {
  const { t } = useTranslation();
  const fetcher = useFetcher<TenantDetailsGenerateShadowInviteResult>();
  const [open, setOpen] = React.useState(false);
  const defaultValue =
    leaseOptions[0]
      ? `lease:${leaseOptions[0].leaseId}`
      : stayOptions[0]
        ? `stay:${stayOptions[0].stayId}`
        : "";
  const [contextValue, setContextValue] = React.useState<string>(defaultValue);
  const [inviteUrl, setInviteUrl] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const prevFetcherState = React.useRef(fetcher.state);
  const hasContext = leaseOptions.length > 0 || stayOptions.length > 0;

  React.useEffect(() => {
    if (open && !contextValue) {
      if (leaseOptions[0]) setContextValue(`lease:${leaseOptions[0].leaseId}`);
      else if (stayOptions[0]) setContextValue(`stay:${stayOptions[0].stayId}`);
    }
  }, [open, leaseOptions, stayOptions, contextValue]);

  React.useEffect(() => {
    if (prevFetcherState.current !== "idle" && fetcher.state === "idle") {
      const data = fetcher.data;
      if (data && typeof data === "object" && "ok" in data) {
        if (data.ok) {
          setInviteUrl(
            data.stayId
              ? buildTenantShadowInviteUrl(data.shadowTenantId, {
                  stayId: data.stayId,
                })
              : buildTenantShadowInviteUrl(
                  data.shadowTenantId,
                  data.leaseId ?? "",
                ),
          );
          setError(null);
        } else {
          setError(t(data.error, { defaultValue: data.error }));
          setInviteUrl("");
        }
      }
    }
    prevFetcherState.current = fetcher.state;
  }, [fetcher.state, fetcher.data, t]);

  const handleGenerate = () => {
    setError(null);
    const parsed = parseContextValue(contextValue);
    if (!parsed) {
      setError(t("Shadow_invite_select_context"));
      return;
    }
    fetcher.submit(
      {
        intent: INTENTS._generate_shadow_invite,
        body: JSON.stringify({
          tenantID: tenantId,
          ...(parsed.kind === "lease"
            ? { leaseID: parsed.id }
            : { stayID: parsed.id }),
          ...(tenantType != null ? { tenantType } : {}),
        }),
      },
      { method: "post", action: formAction },
    );
  };

  const loading = fetcher.state !== "idle";
  const selectOptions = [
    ...leaseOptions.map((option) => ({
      value: `lease:${option.leaseId}`,
      label: option.label,
    })),
    ...stayOptions.map((option) => ({
      value: `stay:${option.stayId}`,
      label: option.label,
    })),
  ];

  return (
    <>
      <Button type="primary" ghost onClick={() => setOpen(true)}>
        {t("Invite_shadow_tenant")}
      </Button>
      <Modal
        title={t("Invite_shadow_tenant")}
        open={open}
        onCancel={() => {
          setOpen(false);
          setInviteUrl("");
          setError(null);
        }}
        footer={null}
        destroyOnClose
        width={560}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t("Invite_shadow_tenant_help")}
          </Typography.Paragraph>
          {!ENVS.tenant_portal_url ? (
            <Alert type="warning" message={t("Tenant_portal_not_configured")} showIcon />
          ) : !hasContext ? (
            <Alert type="info" message={t("Shadow_invite_no_context")} showIcon />
          ) : (
            <>
              <div>
                <Typography.Text strong>{t("Shadow_invite_context")}</Typography.Text>
                <Select
                  style={{ width: "100%", marginTop: 8 }}
                  value={contextValue || undefined}
                  options={selectOptions}
                  onChange={setContextValue}
                  placeholder={t("Shadow_invite_select_context")}
                />
              </div>
              <Button type="primary" onClick={handleGenerate} loading={loading} block>
                {t("Generate_invite_link")}
              </Button>
              {error ? <Alert type="error" message={error} showIcon /> : null}
              {inviteUrl ? (
                <CustomTextField value={inviteUrl} copyable />
              ) : null}
            </>
          )}
        </Space>
      </Modal>
    </>
  );
};
